import * as React from 'react'

import {
  DragLayerMonitorProps,
  DropOptions,
  NodeModel,
  PlaceholderRenderParams,
  RenderParams,
  Tree as ReactDndTree,
  useDragOver,
} from '@minoru/react-dnd-treeview'
import { action, autorun, IReactionDisposer, observable } from 'mobx'
import { observer } from 'mobx-react'

import { WorkflowStepType } from '~/client/graph/types.generated'
import { getFieldTypeCaption } from '~/client/src/desktop/constants/formFieldsCaptionMap'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'

import Breadcrumb from '../FormTypeConfigurator/components/Breadcrumb'
import FieldMappingScreenStore, {
  IImportedField,
  TREE_ROOT_ID,
} from './FieldMappingScreen.store'
import ImportedFieldRow from './ImportedFieldRow'

interface IProps {
  draftType: WorkflowType
  onComplete(): void
  onBack(): void
  onPreviewUpdate(template: WorkflowType): void
}

const DEPTH_PADDING = 24
const DROP_TARGET_OFFSET = 10

@observer
export default class FieldMappingScreen extends React.Component<IProps> {
  private readonly store: FieldMappingScreenStore
  @observable private collapsedSteps = observable.set<number>()
  private readonly disposePreviewReaction: IReactionDisposer

  public constructor(props: IProps) {
    super(props)
    this.store = new FieldMappingScreenStore(props.draftType)

    this.disposePreviewReaction = autorun(() => {
      props.onPreviewUpdate(this.store.buildCurrentPermitType())
    })
  }

  public componentWillUnmount(): void {
    this.disposePreviewReaction()
  }

  public render(): JSX.Element {
    return (
      <div className="tw:flex tw:flex-col tw:h-full tw:overflow-hidden">
        {this.actionBar}
        <div className="tw:flex-1 tw:overflow-y-auto tw:px-5 tw:py-4">
          {this.infoBanner}
          {this.validationBanner}
          {this.allImportedFields}
        </div>
        <ConfirmDialog
          useRedConfirmationButton
          title={Localization.translator.deleteField}
          doneTitle={Localization.translator.delete}
          isOpen={!!this.store.pendingDeleteFieldId}
          onDoneClicked={this.store.confirmDeleteField}
          onCancelClicked={this.store.hideDeleteConfirmation}
        >
          {Localization.translator.deleteFieldQuestion}
        </ConfirmDialog>
      </div>
    )
  }

  private get actionBar(): JSX.Element {
    return (
      <div className="tw:flex tw:items-center tw:py-3 tw:px-5 tw:select-none tw:border-b tw:border-border">
        <div className="tw:flex tw:flex-1 tw:items-center tw:overflow-hidden tw:mr-5">
          <Breadcrumb
            label={Localization.translator.workflows}
            onClick={this.props.onBack}
          />
          <Breadcrumb label={this.props.draftType.name} />
          <Breadcrumb label="Review Fields" isLast />
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Button
            onClick={this.handleContinue}
            disabled={!this.store.hasRequiredFields}
          >
            Continue to Editor
          </Button>
        </div>
      </div>
    )
  }

  private get infoBanner(): JSX.Element {
    return (
      <div className="tw:bg-muted tw:border tw:border-border tw:rounded-md tw:p-3 tw:mb-5">
        <p className="tw:text-sm tw:text-muted-foreground">
          Review the field types assigned by the AI. Use the type dropdown to
          change a field&apos;s type and the step dropdown to move it to a
          different step. Drag fields to reorder them within a step or nest them
          under Section fields. Primary fields can only appear once and must
          remain in the first step.
        </p>
      </div>
    )
  }

  private get validationBanner(): JSX.Element {
    if (this.store.hasRequiredFields) return null

    const missing = this.store.missingRequiredFieldNames

    return (
      <div className="tw:bg-destructive/10 tw:border tw:border-destructive/30 tw:rounded-md tw:p-3 tw:mb-5 tw:flex tw:items-center tw:justify-between tw:gap-3">
        <p className="tw:text-sm tw:text-destructive">
          The following required fields are missing:{' '}
          <span className="tw:font-medium">{missing.join(', ')}</span>. Assign
          them to a field using the type dropdown or add them.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="tw:shrink-0 tw:border-destructive/30 tw:text-destructive tw:hover:bg-destructive/10"
          onClick={this.store.addMissingRequiredFields}
        >
          Add missing fields
        </Button>
      </div>
    )
  }

  private get allImportedFields(): JSX.Element {
    const { fieldsByStep } = this.store
    const steps = this.props.draftType.workflowSteps

    return (
      <div>
        <h3 className="tw:text-sm tw:font-semibold tw:text-foreground tw:mb-3">
          All Imported Fields
        </h3>
        {steps.map((step, stepIndex) => {
          const fields = fieldsByStep.get(stepIndex)
          if (!fields?.length) return null

          return this.renderStepGroup(step.type, stepIndex, fields)
        })}
      </div>
    )
  }

  private renderStepGroup(
    stepType: WorkflowStepType,
    stepIndex: number,
    fields: IImportedField[],
  ): JSX.Element {
    const isCollapsed = this.collapsedSteps.has(stepIndex)
    const stepName = getWorkflowStepDisplayName(stepType)

    return (
      <div key={stepIndex} className="tw:mb-4 tw:last:mb-0">
        <div
          className="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-2 tw:bg-muted/50 tw:border tw:border-border tw:rounded-t-md tw:cursor-pointer tw:select-none tw:hover:bg-muted/70 tw:transition-colors"
          onClick={() => this.toggleStepCollapse(stepIndex)}
        >
          <span
            className={`tw:text-xs tw:transition-transform ${
              isCollapsed ? '' : 'tw:rotate-90'
            }`}
          >
            &#9654;
          </span>
          <span className="tw:text-sm tw:font-medium tw:text-muted-foreground">
            {stepName}
          </span>
          <span className="tw:text-xs tw:text-muted-foreground/60">
            ({fields.length} fields)
          </span>
        </div>

        {!isCollapsed && (
          <div className="tw:border tw:border-border tw:border-t-0 tw:rounded-b-md">
            <ReactDndTree
              classes={{
                root: 'tw:list-none tw:p-0 tw:m-0',
                draggingSource: 'tw:opacity-30',
                placeholder: 'tw:relative',
                dropTarget: 'tw:bg-primary/5 tw:rounded-md',
              }}
              rootId={TREE_ROOT_ID}
              tree={this.store.treeDataForStep(stepIndex)}
              sort={false}
              initialOpen
              insertDroppableFirst={false}
              dropTargetOffset={DROP_TARGET_OFFSET}
              canDrop={this.canDropField}
              render={this.renderNodeRow}
              placeholderRender={this.renderPlaceholder}
              dragPreviewRender={this.renderDragPreview}
              onDrop={newTree => this.store.onTreeDrop(stepIndex, newTree)}
            />
          </div>
        )}
      </div>
    )
  }

  private renderNodeRow = (
    node: NodeModel<IImportedField>,
    { depth, isOpen, onToggle }: RenderParams,
  ): JSX.Element => {
    const dragOverProps = useDragOver(node.id, isOpen, onToggle)

    return (
      <div
        style={{ paddingInlineStart: depth * DEPTH_PADDING }}
        {...dragOverProps}
      >
        <ImportedFieldRow field={node.data} store={this.store} />
      </div>
    )
  }

  private renderPlaceholder = (
    _node: NodeModel<IImportedField>,
    { depth }: PlaceholderRenderParams,
  ): JSX.Element => {
    return (
      <div
        className="tw:absolute tw:h-0.5 tw:bg-primary tw:rounded-full tw:right-0"
        style={{ left: depth * DEPTH_PADDING }}
      />
    )
  }

  private renderDragPreview = ({
    item,
  }: DragLayerMonitorProps<IImportedField>): JSX.Element | null => {
    if (!item.data) return null
    const effectiveType = this.store.getEffectiveType(item.data.id)

    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:bg-muted tw:rounded-lg tw:max-w-40 tw:h-9 tw:px-2.5 tw:text-sm tw:font-medium tw:shadow-md">
        <PermitFieldIcon fieldName={effectiveType} />
        <span className="tw:truncate">
          {this.store.getEffectiveCaption(item.data.id) ||
            getFieldTypeCaption(effectiveType)}
        </span>
      </div>
    )
  }

  private canDropField = (
    tree: NodeModel<IImportedField>[],
    options: DropOptions<IImportedField>,
  ): boolean => {
    return this.store.canDropField(
      tree,
      options.dragSource,
      options.dropTargetId?.toString(),
      options.dropTarget,
    )
  }

  @action.bound
  private toggleStepCollapse(stepIndex: number): void {
    if (this.collapsedSteps.has(stepIndex)) {
      this.collapsedSteps.delete(stepIndex)
    } else {
      this.collapsedSteps.add(stepIndex)
    }
  }

  private handleContinue = (): void => {
    this.store.applyChangesToDraft()
    this.props.onComplete()
  }
}
